import React, { useEffect, useState } from 'react';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Card from '../../muiComponents/Card';
import Grid from '../../muiComponents/Grid';
import GridChild from '../../muiComponents/GridChild';
import TextInput from '../../muiComponents/TextInput/TextInput';
import IconButton from '../../muiComponents/IconButton';
import DeletePopUp from '../../property-partners-portal/ownerPortal/yourProperties/DeletePopUp';
import { commonIcons } from '../../utils/commonVariables/commonIcons';
import { useAccountInformation } from '../../contexts/AccountInformationContext';
import {
    ContactState,
    editableContactFields,
    ProfileViewMode,
} from '../../types/profile/profileTypes';
import useTranslate from '../../customHooks/useTranslate';
import { useNotification } from '../../contexts/NotificationsContext';
import api from '../../services/api';
import {
    adaptAccountContactsStateToDDBB,
    adaptAccountInformationDDBBToState,
} from '../../services/profile/accountInformationAdapters';
import endpoints from '../../utils/commonVariables/endpoints';
import { savableEntities } from '../../utils/commonVariables/entityKeys';
import { FeedbackMessages } from '../../utils/commonVariables/messages';
import convertToOvariant from '../../utils/convertToOvariant';
import accountInformationViewModel from '../../utils/viewModelStructures/accountInformation';
import { validateForm } from '../../utils/validateForms';
import {
    hasPendingChanges,
    isAdded,
    isChanged,
    isDeleted,
} from '../../utils/collectionPersistence';
import {
    doubleXL_ColumnGrid,
    full_ColumnGrid,
    gridSpacing20,
    singleXL_DoubleLG_ColumnGrid,
} from '../../utils/commonVariables/grid';

type ContactFormErrors = Partial<Record<keyof ContactState, string>>;

const EditView = () => {
    const {
        accountInfo,
        detailContact,
        handleDetailContact,
        handleEditContact,
        setAccountInfo,
        handleViewModeChange,
    } = useAccountInformation();
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const [errors, setErrors] = useState<ContactFormErrors>({});
    const [isValidated, setIsValidated] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeletePopUpOpen, setIsDeletePopUpOpen] = useState(false);
    const isExistingContact =
        !!detailContact &&
        accountInfo.Contacts.some(contact => contact.Id === detailContact.Id);

    useEffect(() => {
        if (!detailContact) {
            handleDetailContact();
        }
    }, [detailContact, handleDetailContact]);

    const getValidationErrors = (contact: ContactState): ContactFormErrors => {
        const fieldsToValidate = {
            Name: { value: contact.Name, validators: ['requiredString'] },
            Email: {
                value: contact.Email,
                validators: ['requiredString', 'email'],
            },
            ...(contact.Phone.trim() && {
                Phone: { value: contact.Phone, validators: ['phone'] },
            }),
        };
        const validationErrors: ContactFormErrors = {};
        for (const validationError of validateForm(fieldsToValidate)) {
            validationErrors[validationError.inputKey as keyof ContactState] =
                validationError.errorMessage;
        }
        return validationErrors;
    };

    const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const field = editableContactFields.find(key => key === name);
        if (field) {
            handleEditContact(field, value);
        }
    };

    const handleValidate = () => {
        if (isValidated && detailContact) {
            setErrors(getValidationErrors(detailContact));
        }
    };

    const handleCancel = () => {
        handleViewModeChange(ProfileViewMode.READ_ONLY);
    };

    const saveContact = async (contact: ContactState) => {
        setIsSaving(true);
        try {
            const adaptedData = adaptAccountContactsStateToDDBB(
                accountInfo.Id,
                contact
            );
            const objToSend = convertToOvariant(
                savableEntities.HotelOwner,
                adaptedData,
                accountInfo.Id,
                accountInformationViewModel
            );
            const response = await api.post(endpoints.commonSave, objToSend);
            if (response.status === 201) {
                setAccountInfo(
                    adaptAccountInformationDDBBToState(response.data)
                );
                setSuccessMessage(
                    isDeleted(contact)
                        ? FeedbackMessages.COMMON_SUCCESS_DELETE
                        : FeedbackMessages.COMMON_SUCCESS_UPDATE
                );
                setIsDeletePopUpOpen(false);
                handleViewModeChange(ProfileViewMode.READ_ONLY);
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!detailContact || isSaving) {
            return;
        }
        if (isAdded(detailContact)) {
            setAccountInfo(prevState => ({
                ...prevState,
                Contacts: prevState.Contacts.filter(
                    contact => contact.Id !== detailContact.Id
                ),
            }));
            setIsDeletePopUpOpen(false);
            handleViewModeChange(ProfileViewMode.READ_ONLY);
        } else {
            await saveContact({ ...detailContact, Deleted: true });
        }
    };

    const handleSend = async () => {
        if (!detailContact || isSaving) {
            return;
        }
        setIsValidated(true);
        const validationErrors = getValidationErrors(detailContact);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length === 0) {
            const isNewContact = isAdded(detailContact);
            if (hasPendingChanges(detailContact)) {
                await saveContact({
                    ...detailContact,
                    __isAdded: isNewContact,
                    __isChanged: isNewContact || isChanged(detailContact),
                });
            } else {
                handleViewModeChange(ProfileViewMode.READ_ONLY);
            }
        }
    };

    return (
        <Card className='padding30 flex-column gap-20'>
            <Grid rowSpacing={gridSpacing20} columnSpacing={gridSpacing20}>
                <GridChild gridProps={doubleXL_ColumnGrid}>
                    <TextInput
                        labelText={'name'}
                        value={detailContact?.Name || ''}
                        errorMessage={errors.Name}
                        id={'Name'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={doubleXL_ColumnGrid}>
                    <TextInput
                        labelText={'position'}
                        value={detailContact?.Position || ''}
                        id={'Position'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'contact_type'}
                        value={detailContact?.ContactType || ''}
                        id={'ContactType'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'your_email'}
                        value={detailContact?.Email || ''}
                        errorMessage={errors.Email}
                        id={'Email'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'phone'}
                        value={detailContact?.Phone || ''}
                        errorMessage={errors.Phone}
                        id={'Phone'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                        onBlur={handleValidate}
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'response_time'}
                        value={detailContact?.ResponseTime || ''}
                        id={'ResponseTime'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                    />
                </GridChild>
                <GridChild gridProps={full_ColumnGrid}>
                    <TextInput
                        labelText={'notes'}
                        value={detailContact?.Notes || ''}
                        id={'Notes'}
                        handleChange={handleTextInput}
                        disabled={isSaving}
                        multiline
                        minRows={3}
                    />
                </GridChild>
            </Grid>
            <CardActions sx={{ justifyContent: 'space-between' }}>
                <div>
                    {isExistingContact && (
                        <IconButton
                            iconName={commonIcons.DELETE_BIN}
                            onClick={() => setIsDeletePopUpOpen(true)}
                            disabled={isSaving}
                            className='delete-icon-button'
                            aria-label={translate('delete')}
                            title={translate('delete')}
                        />
                    )}
                </div>
                <div className='flex gap-10'>
                    <Button
                        variant='outlined'
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSend}
                        disabled={isSaving}
                    >
                        {translate('save')}
                    </Button>
                </div>
            </CardActions>
            {isDeletePopUpOpen && (
                <DeletePopUp
                    open={isDeletePopUpOpen}
                    onSave={handleDelete}
                    onCancel={() => setIsDeletePopUpOpen(false)}
                    disableEscapeKeyDown={isSaving}
                />
            )}
        </Card>
    );
};

export default EditView;
